import React, { useEffect, useState } from 'react';
import { ThemedView, ThemedText, Theme, themeColorSet } from '@app/theme';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import Button from '@app/sharedComponents/Button/Button';
import gs from '../../../styles/globalStyles';
import ProgressBar from '@app/sharedComponents/ProgressBar/ProgressBar';
import { FetchPortfolioModalType } from '../type';
import { WarningCircle } from '@app/sharedComponents/Icons/Icons';
import { useTheme } from '@shopify/restyle';

export interface FetchPortfolioModalProps {
    title?: string;
    subtitle?: string;
    primaryCtaTitle?: string;
    tertiaryCTATitle?: string;
    primaryCtaAction?: () => void;
    tertiaryCTAAction?: () => void;
    visible?: boolean;
    type?: FetchPortfolioModalType;
    loadingTime?: number;
    onModalClose?: () => void;
    loaderText?: string;
    errorCode?: string;
}

const FetchPortfolioModals: React.FC<FetchPortfolioModalProps> = ({
    title,
    subtitle,
    primaryCtaTitle,
    tertiaryCTATitle,
    primaryCtaAction,
    tertiaryCTAAction,
    visible = false,
    type = FetchPortfolioModalType.IMMEDIATE,
    loadingTime,
    onModalClose,
    loaderText,
    errorCode
}) => {
    const [_show, setShowModal] = useState(false);
    const {colors} = useTheme<Theme>()

    useEffect(() => {
        if (title === null || title === undefined || title === 'null') {
            setShowModal(false);
        } else {
            setShowModal(visible);
        }
    }, [visible, title]);

    return (
        <BottomSheetContainer
            isVisible={visible}
            showCloseIcon={type !== FetchPortfolioModalType.LOADER}
            onClose={() => {
                setShowModal(false);
                onModalClose && onModalClose();
            }}
            closeOnBackdropPress={false}
            borderTopRightRadius={10}
            borderTopLeftRadius={10}
            containerStyle={gs.setBg('#fefefe')}
            close={() => {
                setShowModal(false);
                onModalClose && onModalClose();
            }}
            dragComponent={null}
        >
            <ThemedView
                style={{
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    padding: 16, 
                    width: '100%',
                }}>
                <ThemedView style={[gs.flexRow, gs.justifyCenter, {marginTop: 8}]} >
                    {/* <Icon name="SYSTEM_STATUS_WARNING_DLS" size="XXXXXXXL" /> */}
                    { errorCode !== 'INVESTOR_KYC_UPDATE' ?
                        <WarningCircle size={60} weight='fill' color={themeColorSet['icon-warning']} /> :
                        <WarningCircle size={48} weight='fill' color={themeColorSet['icon-error']} />
                    }
                </ThemedView>

                {title && title !== null && title !== 'null' && (
                    <>
                        <ThemedView style={[gs.setMarginTop(24)]}/>
                        <ThemedText
                            variant='h5s'
                            color="text-primary"
                            textAlign={'center'}
                        >
                            {title}
                        </ThemedText>
                    </>
                )}

                {type !== FetchPortfolioModalType.LOADER && subtitle && subtitle !== null && subtitle !== 'null' && (
                    <>
                        <ThemedView style={[gs.setMarginTop(8)]}/>
                        <ThemedView 
                            style={{
                                    width: '100%',
                            }}
                        >
                            <ThemedText
                                variant='b3r'
                                color="text-primary"
                                style={{
                                    backgroundColor: errorCode === 'INVESTOR_KYC_UPDATE' ? 'transparent' : colors['bg-fill-warning'],
                                    padding: errorCode === 'INVESTOR_KYC_UPDATE' ? 0 : 12,
                                    borderRadius: 8,
                                    width: '100%',
                                    textAlign: 'center',
                                }}
                            >
                                {subtitle}
                            </ThemedText>
                        </ThemedView>
                    </>
                )}

                {type !== FetchPortfolioModalType.LOADER && primaryCtaAction && primaryCtaTitle && (
                    <>
                        <ThemedView style={[gs.setMarginTop(24)]}/>
                        <Button
                            type="SOLID"
                            size="MD"
                            label={primaryCtaTitle || 'Test'}
                            bg="bg-action-primary"
                            textColor="text-on-action-primary"
                            handlePress={() => {
                                primaryCtaAction &&
                                primaryCtaAction();
                            }}
                        />
                    </>
                )}

                {type !== FetchPortfolioModalType.LOADER && tertiaryCTATitle && tertiaryCTAAction && (
                    <ThemedView style={{width: '100%', marginTop: primaryCtaAction && primaryCtaTitle ? 12 : 24}}>
                        <Button
                            type='OUTLINED'
                            size="MD"
                            label={tertiaryCTATitle || 'Test'}
                            bg="bg-action-primary"
                            textColor="text-on-action-primary"
                            handlePress={() => {
                                tertiaryCTAAction &&
                                tertiaryCTAAction();
                            }}
                        />
                    </ThemedView>
                )}

                {type === FetchPortfolioModalType.LOADER && (
                    <>
                        <ThemedView style={[gs.setMarginTop(24)]}/>
                        {loaderText && (
                            <ThemedView style={[gs.setPaddingBottom(8)]}>
                                <ThemedText
                                    variant='b4m'
                                    color="text-primary"
                                    // textAlign={TextAlignTokens.center}
                                >
                                    {loaderText}
                                </ThemedText>
                            </ThemedView>
                        )}
                        <ProgressBar
                            color={colors['bg-action-primary']}
                            unfilledColor={colors['bg-fill-emphasis-primary-1']}
                            loadingTime={loadingTime}
                        />
                    </>
                )}
                {
                    errorCode === 'INVESTOR_KYC_UPDATE' && ( 
                        <>
                            <ThemedView  mt='sm'
                                style={{
                                        width: '100%',
                                        marginTop: 12,
                                        backgroundColor: colors['bg-fill-warning'],
                                        padding: 12,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: colors['border-warning'],
                                }}
                            >
                                <ThemedText
                                    variant='b4b'
                                    color='text-warning'
                                    textAlign='center'
                                >
                                {'Already updated your KYC?'}
                                </ThemedText>
                                <ThemedText
                                        variant='b4r'
                                        color='text-warning'
                                        textAlign='center'
                                    >
                                    {'It generally takes 2-3 days to reflect, please wait before retrying fetch'}
                                </ThemedText>
                            </ThemedView>
                        </>
                    )
                }
            </ThemedView>
        </BottomSheetContainer>
    );
};

export default FetchPortfolioModals; 